import { createContext, ReactNode, useContext, useState } from 'react';
import { css, styled } from 'styled-components';

import { theme } from '../../styles';
import { ChevronIconOld, CircleExclamationIcon, Loading, TriangleExclamationIcon } from '../../assets/icons';
import { Box, Flex } from '../Layout';
import { HeadCellSortState } from './helpers';
import { Colors, createResponsiveSpacingStyle, ResponsiveSpacing } from '../../utils';
import { Animate } from '../Animate';
import { Text } from '../Text';
import { NoStyleButton } from '../NoStyleButton';

const StyledTable = styled.table<{ $layoutFixed: boolean; outerBorder: boolean }>`
  border-collapse: collapse;
  margin: 20px 0;
  font-size: 0.9em;
  width: 100%;
  overflow: hidden;
  table-layout: ${props => (props.$layoutFixed ? 'fixed' : 'auto')};
  /** It's tricky to get both rounded corners AND an outer border with bordered rows on a table. So making them exclusive for now. */
  ${props => !props.outerBorder && 'border-radius: 3px 3px 0 0;'}
  ${props => props.outerBorder && `border: 1px solid ${theme.colors.black20};`}
`;

const BorderedRow = styled.tr<{ showBorder?: boolean; isExpanded?: boolean; fullBorderColor?: Colors }>`
  ${props =>
    props.showBorder &&
    css`
      border-bottom: 1px solid ${props.isExpanded ? theme.colors.white : theme.colors.black20};
    `}
  ${props =>
    props.fullBorderColor &&
    css`
      border: 1px solid ${props.fullBorderColor ? theme.colors[props.fullBorderColor] : theme.colors.black20};
      td {
        border-top: 1px solid ${props.fullBorderColor ? theme.colors[props.fullBorderColor] : theme.colors.black20};
      }
    `}
`;

type StyledBorderedRowProps = {
  testId?: string;
  isHeadRow?: boolean;
  withExpandableRows?: boolean;
  isExpanded?: boolean;
  usePointerCursor?: boolean;
  customRowColor?: Colors;
  showBorder?: boolean;
  compact?: boolean;
};

const Row = styled(BorderedRow)<StyledBorderedRowProps>`
  background-color: ${theme.colors.white};
  &:hover {
    background-color: ${theme.colors.babyGreenHover};
  }
  background-color: ${props =>
    props.customRowColor
      ? theme.colors[props.customRowColor]
      : props.isHeadRow
        ? theme.colors.petroleumGreen
        : props.isExpanded
          ? theme.colors.babyGreen
          : ''}!important;
  color: ${props => (props.isHeadRow ? `white` : 'black')};
  text-align: left;
  font-weight: bold;
  ${props => !props.isHeadRow && props.usePointerCursor && 'cursor: pointer;'};
  ${props =>
    !props.compact &&
    css`
      height: 56px; // behaves like min-height
    `};
`;

const StyledExpandedCell = styled.td<{ isExpanded: boolean; customRowColor?: Colors }>`
  background-color: ${props =>
    props.customRowColor
      ? theme.colors[props.customRowColor]
      : props.isExpanded
        ? `${theme.colors.babyGreen}`
        : ''}!important;
  padding: ${props => (props.isExpanded ? '0px 10px 10px 10px' : '0px')};
  width: 100%;
`;

const StyledErrorCell = styled.td`
  background-color: ${theme.colors['babyPink']};
  border-left: 1px solid ${theme.colors['burgundyDark']};
  border-right: 1px solid ${theme.colors['burgundyDark']};
  border-bottom: 1px solid ${theme.colors['burgundyDark']};
  text-align: right;
  padding: 10px 20px;
`;
const StyledWarningCell = styled.td`
  background-color: ${theme.colors['babyYellow']};
  border-left: 1px solid ${theme.colors['brown']};
  border-right: 1px solid ${theme.colors['brown']};
  border-bottom: 1px solid ${theme.colors['brown']};
  text-align: right;
  padding: 10px 20px;
`;

const ExpandedRowBody = styled.div`
  background-color: ${theme.colors.white};
`;

export const tableCellPaddingVertical = 10;
export const tableCellPaddingHorizontal = 14;

const cellPadding = `${tableCellPaddingVertical}px ${tableCellPaddingHorizontal}px`;
const cellStyle = css<{ textAlign?: 'left' | 'right' | 'center' }>`
  text-align: ${props => props.textAlign};
  font-family:
    'Myriad',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    'Roboto',
    'Oxygen',
    'Ubuntu',
    'Cantarell',
    'Fira Sans',
    'Droid Sans',
    'Helvetica Neue',
    sans-serif;
`;
const Cell = styled.td<{ textAlign?: 'left' | 'right' | 'center' }>`
  ${cellStyle}
  padding: ${cellPadding};
`;

const expandCellStyle = css`
  width: 10px;
  padding-right: 14px;
  text-align: right;
`;
const ExpandCell = styled.td`
  ${expandCellStyle}
`;
const HeadExpandCell = styled.th`
  ${expandCellStyle}
`;

const TableContext = createContext({
  withExpandableRows: false,
  withBorderedRows: false,
  compact: false,
});

type TableProps = {
  /** Show Chevron and make rows expandable */
  withExpandableRows?: boolean;
  /** Show borders between rows */
  withBorderedRows?: boolean;
  /** Show border around table, this also turns off rounded edges */
  outerBorder?: boolean;
  /** Thinner table rows */
  compact?: boolean;
  /** Sets table-layout: fixed; which allows for controlling column widths  */
  layoutFixed?: boolean;
  children: ReactNode;
};

export const Table = ({
  withExpandableRows = false,
  withBorderedRows = false,
  outerBorder = false,
  compact = false,
  layoutFixed = false,
  children,
}: TableProps) => {
  return (
    <TableContext.Provider value={{ withExpandableRows, withBorderedRows, compact }}>
      <StyledTable $layoutFixed={layoutFixed} outerBorder={outerBorder}>
        {children}
      </StyledTable>
    </TableContext.Provider>
  );
};

Table.Head = ({ children }: any) => {
  return <thead>{children}</thead>;
};

Table.Body = ({ children }: any) => {
  return <tbody>{children}</tbody>;
};

type HeadRowProps = {
  children: any;
  fullBorderColor?: Colors;
};

type RowProps = HeadRowProps & {
  onClick?: (isExpanded: boolean) => void;
  isInitiallyExpanded?: boolean;
  customRowColor?: Colors;
  withWarning?: string;
  withError?: string;
  testId?: string;
};

Table.HeadRow = ({ children, fullBorderColor }: HeadRowProps) => {
  const { withExpandableRows, compact } = useContext(TableContext);

  return (
    <Row isHeadRow={true} compact={compact} fullBorderColor={fullBorderColor}>
      {children}
      {withExpandableRows && <HeadExpandCell />}
    </Row>
  );
};

/** Get the state of a row. Context must used within a child of <Table.Row>.  Usage:
 * const { isRowExpanded } = useContext(TableRowContext); */
export const TableRowContext = createContext({
  isRowExpanded: false,
});

Table.Row = ({
  onClick,
  isInitiallyExpanded = false,
  customRowColor,
  withWarning,
  withError,
  children,
  fullBorderColor,
  testId,
}: RowProps) => {
  const [isExpanded, setIsExpanded] = useState(isInitiallyExpanded);
  const [hover, setHover] = useState(false);
  const { withExpandableRows, withBorderedRows, compact } = useContext(TableContext);

  const onToggle = () => {
    if (withExpandableRows) {
      setIsExpanded(prevIsExpanded => !prevIsExpanded);
    }
    if (onClick) {
      onClick(isExpanded);
    }
  };

  const rowCells = Array.isArray(children)
    ? children.filter((child: any) => child && child.key !== 'body')
    : [children];

  return (
    <TableRowContext.Provider value={{ isRowExpanded: isExpanded }}>
      <Row
        data-testid={testId}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={onToggle}
        isExpanded={isExpanded}
        withExpandableRows={withExpandableRows}
        usePointerCursor={withExpandableRows || !!onClick}
        customRowColor={customRowColor}
        showBorder={withBorderedRows && !isExpanded}
        compact={compact}
        fullBorderColor={
          fullBorderColor ? fullBorderColor : withError ? 'burgundyDark' : withWarning ? 'brown' : undefined
        }
      >
        {rowCells}
        {withExpandableRows && <ExpandCell>{(hover || isExpanded) && <Chevron rotate={isExpanded} />}</ExpandCell>}
      </Row>

      {withExpandableRows && (
        <BorderedRow showBorder={withBorderedRows && isExpanded} isExpanded={true}>
          <StyledExpandedCell colSpan={rowCells.length + 1} isExpanded={isExpanded} customRowColor={customRowColor}>
            {isExpanded && (
              <ExpandedRowBody>
                {children.filter((child: { key: string }) => child && child.key === 'body')}
              </ExpandedRowBody>
            )}
          </StyledExpandedCell>
        </BorderedRow>
      )}

      {withError && (
        <BorderedRow>
          <StyledErrorCell colSpan={rowCells.length + (withExpandableRows ? 1 : 0)}>
            <Flex justifyContent={['flex-start', 'flex-end', 'flex-end']} flexDirection={['row-reverse', 'row', 'row']}>
              <TriangleExclamationIcon color={'black'} width={22} marginLeft={10} marginRight={[0, 10, 10]} />
              <Text>{withError}</Text>
            </Flex>
          </StyledErrorCell>
        </BorderedRow>
      )}
      {withWarning && (
        <BorderedRow>
          <StyledWarningCell colSpan={rowCells.length + (withExpandableRows ? 1 : 0)}>
            <Flex justifyContent={['flex-start', 'flex-end', 'flex-end']} flexDirection={['row-reverse', 'row', 'row']}>
              <CircleExclamationIcon color={'black'} width={22} marginLeft={10} marginRight={[0, 10, 10]} />
              <Text>{withWarning}</Text>
            </Flex>
          </StyledWarningCell>
        </BorderedRow>
      )}
    </TableRowContext.Provider>
  );
};

type CellProps = {
  textAlign?: 'left' | 'right' | 'center';
  colSpan?: number;
  cellWidth?: ResponsiveSpacing;
  hoverable?: boolean;
  padding?: string;
  children?: any;
};

Table.Cell = ({ textAlign = 'left', children, colSpan }: CellProps) => {
  if (!children) {
    return <Cell textAlign={textAlign} colSpan={colSpan} />;
  }
  const cellContent =
    typeof children === 'string' ? <Text style={{ fontSize: '14px' }}>{children}</Text> : { ...children };
  return (
    <Cell textAlign={textAlign} colSpan={colSpan}>
      {cellContent}
    </Cell>
  );
};

const HeadCell = styled.th<CellProps>`
  ${cellStyle}
  ${props => props.padding && `padding: ${props.padding};`};
  ${props => props.cellWidth && `${createResponsiveSpacingStyle('width', props.cellWidth)};`};
  white-space: nowrap;
  ${props =>
    props.hoverable &&
    css`
      &:hover {
        background-color: ${theme.colors.petroleumGreenHover};
      }
      &:hover:not(:focus-within),
      &:active {
        ${ChevronWrapper} {
          opacity: 1;
        }
      }
      cursor: pointer;
    `};
`;

type HeadCellProps = {
  textAlign?: 'left' | 'right' | 'center';
  state?: HeadCellSortState;
  onToggle?: (id: string) => void;
  width?: ResponsiveSpacing;
  sortId?: string;
  children?: any;
};

Table.HeadCell = ({ textAlign = 'left', state, onToggle, sortId, width, children }: HeadCellProps) => {
  const cellContent =
    typeof children === 'string' ? (
      <Text color="white" fontWeight="semibold" style={{ fontSize: '14px' }} renderAs="span">
        {children}
      </Text>
    ) : (
      { ...children }
    );

  // If sortable
  if (onToggle) {
    return (
      <HeadCell cellWidth={width} hoverable={true} onClick={() => onToggle(sortId || '')}>
        <CellButton textAlign={textAlign}>
          {cellContent}
          <Chevron
            marginLeft="5px"
            rotate={state === HeadCellSortState.Asc}
            inactive={state === HeadCellSortState.None}
            color="white"
          />
        </CellButton>
      </HeadCell>
    );
  }

  return (
    <HeadCell textAlign={textAlign} cellWidth={width} padding={cellPadding}>
      {cellContent}
    </HeadCell>
  );
};

export const TableLoadingRow = () => (
  <Row customRowColor="white">
    <Table.Cell colSpan={8}>
      <Flex justifyContent="center" paddingBottom={20} paddingTop={20}>
        <Loading color="black20" />
      </Flex>
    </Table.Cell>
  </Row>
);

Table.ExpandedRowBodyWrapper = ({ children }: { children: React.ReactElement }) => {
  return (
    <Flex justifyContent="center" paddingBottom={70} paddingTop={50} paddingLeft={20} paddingRight={20}>
      <Box maxWidth={578} width="100%" paddingLeft={[10, 30, 0]} paddingRight={[10, 30, 0]}>
        {children}
      </Box>
    </Flex>
  );
};

type ChevronProps = {
  rotate: boolean;
  inactive?: boolean;
  color?: 'white' | 'black';
  marginLeft?: string;
};

const Chevron = ({ rotate, inactive = false, color = 'black', marginLeft }: ChevronProps) => {
  return (
    <ChevronWrapper marginLeft={marginLeft} opacity={inactive ? 0.4 : 1}>
      <Animate.Rotate isTriggered={rotate} degrees={180}>
        <ChevronIconOld variant="thin" direction="downward" width={10} color={color} />
      </Animate.Rotate>
    </ChevronWrapper>
  );
};

const ChevronWrapper = styled.span<{ marginLeft?: string; opacity?: number }>`
  margin-left: ${props => props.marginLeft};
  opacity: ${props => props.opacity ?? 1};
`;
// const NoStyleButton = styled.button`
//   background: none;
//   color: inherit;
//   border: none;
//   padding: 0;
//   font: inherit;
//   cursor: pointer;
//   outline: inherit;
// `;

const CellButton = styled(NoStyleButton)<CellProps>`
  ${cellStyle}
  padding: ${cellPadding};
  width: 100%;
  &:focus-visible {
    border-radius: 2px;
    border: solid 2px ${theme.colors.black};
  }
`;
